import { useState, useEffect } from "react";
import { LinkButton, Button, MaterialIcon, LoadingSpinner } from "./UiComponents";
import { Link, useNavigate, useMatch } from "react-router-dom";
import { useAppContext } from "../utils/context/app-context";
import { TextArea } from "../components/FormComponents";
import parse from "date-fns/parse";
import format from "date-fns/format";
import isValid from "date-fns/isValid";
import { Modal } from "react-bootstrap";
import { useUserIsReadOnly } from "./userFunctions";

export const CourseTile = ({ course, showViewButton = true }) => {
	if (!course) {
		return null;
	}

	const courseDayCount = course.dates.length;
	const courseDayText = courseDayCount > 1 ? "days" : "day";

	const dates = buildDateOutline(course);

	return (
		<div className="course-tile">
			<div>
				<h3 className="course-tile__title">
					{course.title} ({courseDayCount} {courseDayText})
				</h3>
				<CourseDetailItem icon="calendar_month" text={dates} />
				{course.client_name != '' && (
					<CourseDetailItem icon="groups" text={course.client_name} />
				)}
				{course.full_venue_text != '' && (
					<CourseDetailItem icon="location_on" text={course.full_venue_text} />
				)}
				<CourseDetailItem icon="schedule" text={buildTime(course)} />
			</div>
			{showViewButton && (
				<LinkButton
					to={`/courses/${course.id}`}
					text="View / Manage"
					type="tertiary"
					className="course-tile__button"
				/>
			)}
		</div>
	);
};

export function buildTime(course) {
	if (!course.start_time) {
		return null;
	}

	try {
		const parsedStartTime = parse(course.start_time, "HH:mm", new Date());
		if (!isValid(parsedStartTime)) {
			return null;
		}

		const startTime = format(parsedStartTime, "h:mma").toLowerCase();
		let time = startTime;

		if (course.end_time) {
			const parsedEndTime = parse(course.end_time, "HH:mm", new Date());
			if (isValid(parsedEndTime)) {
				const endTime = format(parsedEndTime, "h:mma").toLowerCase();
				time += ` - ${endTime}`;
			}
		}

		return time;
	} catch (error) {
		console.error("Error parsing time:", error);
		return null;
	}
}

export function buildDateOutline(course) {
	if (!course) {
		return "";
	}

	// Mon, 23rd Jun 2023
	let dateFormatWithoutYear = "dd/MM/yyyy";
	let dateFormat = "dd/MM/yyyy";

	// Check start date can be parse, if not return '-'
	if (!parse(course.start_date, "yyyy-MM-dd", new Date())) {
		return "-";
	}

	const start = parse(course.start_date, "yyyy-MM-dd", new Date());
	const end = parse(course.end_date, "yyyy-MM-dd", new Date());

	const sameStartEnd = start.getTime() === end.getTime();
	const sameStartEndYear = start.getFullYear() === end.getFullYear();

	// If sameStartEnd, just show the start date formatted with dateFormat. If different start and end dates, show both formatted with end formatted with dateFormat. If different start and end dates and different years, show both formatted with end formatted with dateFormat.
	let date_text = "";
	if (sameStartEnd) {
		date_text = format(start, dateFormat);
	} else if (sameStartEndYear) {
		date_text = `${format(start, dateFormatWithoutYear)} - ${format(
			end,
			dateFormat
		)}`;
	} else {
		if (isValid(start)) {
			date_text = `${format(start, dateFormat)} - `;
		}

		if (isValid(end)) {
			date_text += format(end, dateFormat);
		}
	}

	return date_text;
}

export const CourseDetailItem = ({ icon, text }) => {
	if (!text) {
		return null;
	}
	return (
		<div className="course-detail-item">
			<MaterialIcon icon={icon} className="course-detail-item__icon" />
			<div className="course-detail-item__text">{text}</div>
		</div>
	);
};

export const CourseHeader = ({ title = null, showQrButton = true }) => {
	const navigate = useNavigate();
	let isCourseHomepage = useMatch("/courses/:id");
	const [showDatesModal, setShowDatesModal] = useState(false);
	const { state, dispatch, api } = useAppContext();
	const current_course = api.getCurrentCourse();
	const current_date = api.getCurrentDate();

	if (!current_course) {
		return null;
	}

	function formatDate() {
		if (!current_date) {
			return null;
		}
		return format(
			parse(current_date.date, "yyyy-MM-dd", new Date()),
			"EE do MMM"
		);
	}

	const titleText = title ? title : formatDate();
	return (
		<>
			<div className="course-header">
				{!isCourseHomepage && (
					<div className="course-header__home-button">
						<Link to={`/courses/${current_course.id}`}>
							<MaterialIcon icon="home" />
						</Link>
					</div>
				)}
				<div className="course-header__title">
					<h1>{titleText}</h1>
				</div>
				<div className="course-header__buttons">
					{current_course && current_course.dates.length > 1 && (
						<Button
							icon="calendar_month"
							type="outline-tertiary"
							className="course-header__button course-header__change-date-button"
							size="xs"
							onClick={() => setShowDatesModal(true)}
						/>
					)}
					{showQrButton && current_course && (
						<Button
							icon="qr_code"
							text="Participant QR Code"
							type="primary"
							className="course-header__button course-header__qr-button"
							size="sm"
							onClick={() =>
								navigate(
									`/courses/${current_course.id}/participant-forms-access`
								)
							}
						/>
					)}
				</div>
			</div>
			<DatesModal
				course={current_course}
				current_date={current_date}
				show={showDatesModal}
				onHide={() => setShowDatesModal(false)}
				onSelect={(date) => {
					api.changeDate(date);
					setShowDatesModal(false);
				}}
			/>
		</>
	);
};

export const DatesModal = ({
	course,
	current_date,
	show,
	onHide,
	onSelect,
}) => {
	if (!course) {
		return null;
	}

	function handleSelect(date) {
		onSelect(date);
	}

	return (
		<Modal show={show} onHide={onHide} size="lg" centered>
			<Modal.Header>
				<Modal.Title>Select Date</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				<div className="dates-modal">
					<div className="dates-modal__buttons">
						{course.dates.map((date) => (
							<div key={date.id}>
								<Button
									date={date}
									size="sm"
									type={
										current_date?.id === date.id
											? "primary"
											: "outline-primary"
									}
									onClick={() => handleSelect(date)}
									text={format(
										parse(date.date, "yyyy-MM-dd", new Date()),
										"EEEE do MMMM"
									)}
								/>
							</div>
						))}
					</div>
				</div>
				<span className="dates-modal__cancel" onClick={onHide}>
					Cancel
				</span>
			</Modal.Body>
		</Modal>
	);
};

export const CourseTaskList = ({ course, date }) => {
	if (!course) {
		return null;
	}

	return (
		<div className="course-task-list">
			<ol>
				<TaskItem text="Ensure Participants have registered." link={`/courses/${course?.id}/attendance-register`} />
				<TaskItem text="Ensure registered Participants have filled in health questionnaires." link={`/courses/${course?.id}/attendance-register`} />
				{(!dateHasRiskAssessment(date) && course.requires_daily_risk_assessment == 1) && (
					<TaskItem text="Complete daily risk assessment." link={`/courses/${course?.id}/daily-risk-assessment`} />
				)}
				{!checkForTeachingLog(date) && (
					<TaskItem text="Complete your teaching log." link={`/courses/${course?.id}/teaching-log`} />
				)}
			</ol>
		</div>
	);
};

export const CourseChecklist = () => {
	const { api, dispatch } = useAppContext();
	const course = api.getCurrentCourse();
	const date = api.getCurrentDate();
	const [showList, setShowList] = useState(true);

	return (
		<div className="course-checklist">
			<div className="course-checklist__header">
				<span className="course-checklist__title">Tutor Checklist</span>
				<button
					className="course-checklist__toggle"
					onClick={() => setShowList(!showList)}>
					<MaterialIcon icon={showList ? "expand_less" : "expand_more"} />
				</button>
			</div>
			{showList && (
				<div className="course-checklist__body">
					{course.requires_daily_risk_assessment == 1 && (
						<CourseChecklistItem
							label="Daily Risk Assessment of Venue"
							checkable={false}
							checked={dateHasRiskAssessment(date)}
							showWarning={riskAssessmentHasFails(date) && dateHasRiskAssessment(date)}
						/>
					)}
					<CourseChecklistItem
						label="Teaching Log Sheet"
						checkable={false}
						checked={checkForTeachingLog(date)}
					/>
					<CourseChecklistItem
						label="Tutor Introduction"
						name="tutor_intro"
					/>
					<CourseChecklistItem
						label="Housekeeping/Safety"
						name="housekeeping"
					/>
					<CourseChecklistItem
						label="Group Introduction"
						name="group_introduction"
					/>
					<CourseChecklistItem
						label="PowerPoint Presentation"
						name="powerpoint"
					/>
					{course.requires_health_questionnaire == 1 && (
						<CourseChecklistItem
							label="Health Questionnaires"
							checkable={false}
							checked={allHealthQuestionnairesChecked(date?.attendance)}
						/>
					)}
					<CourseChecklistItem
						label="Techniques Demonstrated"
						checkable={false}
						checked={allPhysicalCompetencyFormsCompleted(course?.students)}
					/>
					<CourseChecklistItem
						label="Breakaway Techniques Assessment"
						checkable={false}
						checked={allPhysicalCompetencyFormsCompleted(course?.students)}
					/>
					{course.requires_written_assessment == 1 && (
						<CourseChecklistItem
							label="PMVA Assessment"
							checkable={false}
							checked={allAssessmentsMarked(course?.students)}
						/>
					)}
					<CourseChecklistItem
						label="PMVA Practical Assessment"
						checkable={false}
						checked={allPhysicalCompetencyFormsCompleted(course?.students)}
					/>
					<CourseChecklistItem
						label="Search Awareness"
						name="search_awareness_checklist"
						checkable={true}
						notApplicable={!courseHasModule(course, "search_awareness")}
					/>
					<CourseChecklistItem
						label="Referral Forms"
						checkable={false}
						checked={allStudentsNeedingReferralsHaveOne(course?.students)}
						notApplicable={!anyStudentsNeedReferrals(course?.students)}
					/>
					<CourseChecklistItem
						label="Evaluation Forms"
						name="evaluation_forms"
					/>
					<CourseChecklistItem
						label="Incidents Recorded"
						name="incident_record"
					/>
				</div>
			)}
		</div>
	);
};

const CourseChecklistItem = ({ label, checked, name, checkable = true, showWarning = false, notApplicable = false }) => {
	const { api, dispatch, state } = useAppContext();
	const isOffline = state.offline;
	const [error, setError] = useState(null);
	const [checking, setChecking] = useState(false);
	const course = api.getCurrentCourse();
	const date = api.getCurrentDate();
	const checklist = date?.checklist;
	let classes = "course-checklist-item";
	const isReadyOnly = useUserIsReadOnly();

	if (notApplicable) {
		classes += " course-checklist-item--not-applicable";
	} else if (checkable && !isReadyOnly && !isOffline) {
		classes += " course-checklist-item--clickable";
	} else {
		classes += " course-checklist-item--not-clickable";
	}

	async function handleChecked() {
		if (!checkable || isReadyOnly || isOffline) {
			return;
		}

		setError(null)
		setChecking(true)
		const current_val = getValue(name);
		const val = current_val ? 0 : 1;
		const dataToPost = {
			course_id: course.id,
			date_id: date.id,
			form_data: {
				[name]: val,
			}
		}

		const { data, error } = await api.fetchData(
			`/forms/tutor-checklist`,
			"POST",
			dataToPost
		);

		setChecking(false)
		if (data?.success) {
			dispatch({
				type: "SET_DATE_CHECKLIST_ITEM",
				payload: {
					course_id: course.id,
					date_id: date.id,
					item_name: name,
					item_val: val,
				},
			});
		} else {
			setError(error || data?.error || "An error occurred")
		}
	}

	function getValue() {
		if (checklist && Object.keys(checklist).includes(name)) {
			let val = checklist[name];
			if (val == 1) {
				return true;
			}
		}

		if (checked) {
			return true;
		}

		return false;
	}

	if (getValue() && !notApplicable) {
		classes += " course-checklist-item--checked";
	}

	if (showWarning && !notApplicable) {
		classes += " course-checklist-item--warning";
	}

	function getIcon() {
		if (notApplicable) {
			return "close";
		}
		if (showWarning) {
			return "warning";
		}
		return getValue() ? "check_box" : "check_box_outline_blank";
	}

	return (
		<>
			<button className={classes} onClick={handleChecked}>
				{!checking && (
					<MaterialIcon
						icon={getIcon()}
						className="course-checklist-item__icon"
					/>
				)}
				{checking && (
					<div className="course-checklist-item__spinner">
						<LoadingSpinner />
					</div>
				)}
				<span className="course-checklist-item__text">{label}</span>
			</button>
			{error && (
				<div className="course-checklist-item__error">{error}</div>
			)}
		</>
	);
};

const TaskItem = ({ text, link = '' }) => {
	return (
		<li>
			<Link to={link}>
				{text}
			</Link>
		</li>
	);
};

export const QuickLink = ({ text, to, icon, type = "standard" }) => {
	let classes = "course-quick-link";
	if (type === "light") {
		classes += " course-quick-link--light";
	}
	return (
		<Link to={to} className={classes}>
			<span className="course-quick-link__icon">
				<MaterialIcon icon={icon} />
			</span>
			<span className="course-quick-link__text">{text}</span>
		</Link>
	);
};

export const CourseBackLink = () => {
	const navigate = useNavigate();
	return (
		<div className="course-back-link">
			<Link
				to=".."
				onClick={(e) => {
					e.preventDefault();
					navigate(-1);
				}}>
				{"<"} Go Back
			</Link>
		</div>
	);
};

export const CourseWrapper = ({ children }) => {
	return (
		<div className="course-wrapper">
			{children}
			<CourseBackLink />
		</div>
	);
};

export const CourseDetailHeader = ({ course }) => {
	const [showDetails, setShowDetails] = useState(false);
	if (!course) {
		return null;
	}

	function toggleDetailsShow() {
		setShowDetails(!showDetails);
	}

	return (
		<div className="header-course-details">
			<div className="header-course-details__top">
				<h2 className="header-course-details__title">{course.title}</h2>
				<span
					className="header-course-details__mobile-header-button"
					onClick={toggleDetailsShow}>
					<MaterialIcon
						icon={showDetails ? "arrow_drop_up" : "arrow_drop_down"}
					/>
				</span>
			</div>
			<div
				className={`header-course-details__details ${showDetails ? "header-course-details__details--visible" : ""
					}`}>

				{course.client_name != '' && (
					<CourseDetailItem icon="groups" text={course.client_name} />
				)}
				<CourseDetailItem
					icon="calendar_month"
					text={buildDateOutline(course)}
				/>
				<CourseDetailItem icon="location_on" text={course.full_venue_text} />
				<CourseDetailItem icon="schedule" text={buildTime(course)} />
			</div>
		</div>
	);
};

export const CourseCommentsBox = () => {
	const { api, dispatch, state } = useAppContext();
	const isOffline = state.offline;
	const course = api.getCurrentCourse();
	const date = api.getCurrentDate();
	const [updatingComments, setUpdatingComments] = useState(false);
	const [commentsUpdated, setCommentsUpdated] = useState(false);
	const [updateError, setUpdateError] = useState(null);
	const isReadyOnly = useUserIsReadOnly();

	useEffect(() => {
		if (commentsUpdated) {
			setTimeout(() => {
				setCommentsUpdated(false);
			}, 2000);
		}
	}, [commentsUpdated]);

	function handleCommentsChanged(val) {
		dispatch({
			type: "UPDATE_COURSE_COMMENTS",
			payload: {
				course_id: course.id,
				comments: val,
			}
		})
	}

	async function saveComments() {
		setUpdateError(null);
		setUpdatingComments(true);
		console.log(course.tutor_comments)
		const dataToPost = {
			course_id: course.id,
			comments: course.tutor_comments,
		}

		const { data, error } = await api.fetchData(
			`/courses/update-tutor-notes`,
			"POST",
			dataToPost
		);

		console.log(data)

		setUpdatingComments(false);
		if (data?.success) {
			setCommentsUpdated(true);
		} else {
			setUpdateError(error || data?.error || "An error occurred")
		}
	}

	function buttonText() {
		if (commentsUpdated) {
			return "Comments Updated!";
		}

		if (updatingComments) {
			return "Updating Comments...";
		}
		return "Update Comments";

	}

	return (
		<div className="course-comments-box">
			<h3>Comments</h3>
			<TextArea
				name="tutor_comments"
				value={course?.tutor_comments ?? ""}
				onChange={(e) =>
					handleCommentsChanged(e.target.value)
				}
				rows={8}
				placeholder="Add comments here...."
				required={true}
			/>
			<Button
				text={buttonText()}
				type="primary"
				buttonType="button"
				disabled={updatingComments || commentsUpdated || isReadyOnly || isOffline}
				onClick={saveComments}
			/>
		</div>
	)
}

export function dateHasRiskAssessment(date) {
	if (!date) {
		return false;
	}
	if (!date.risk_assessment) {
		return false;
	}
	return true;
}

export function riskAssessmentHasFails(date) {
	if (!date) {
		return false;
	}

	if (!date.risk_assessment) {
		return false;
	}

	let answers = date.risk_assessment.answers;

	for (const [key, value] of Object.entries(answers)) {
		if (value == 2) {
			return true;
		}
	}

	return false;
}

export function checkForTeachingLog(date) {
	if (!date) {
		return false;
	}

	if (date?.teaching_log) {
		return true;
	}

	return false;
}

export function allStudentsHaveCompletedEvaluations(students) {
	if (!students) {
		return false;
	}

	if (Array.isArray(students) && students.length === 0) {
		return false;
	}

	for (const [key, value] of Object.entries(students)) {
		if (!value.has_evaluation_form) {
			return false;
		}
	}

	return true;
}

export function allStudentsNeedingReferralsHaveOne(students) {
	if (!students) {
		return false;
	}

	if (Array.isArray(students) && students.length === 0) {
		return false;
	}

	for (const [key, value] of Object.entries(students)) {
		let isReferred = value.is_referred || (value.has_physical_competency == 1 && value.physical_competency_result == 2);
		let hasReferral = (Array.isArray(value.competency_referrals) && value.competency_referrals.length > 0) || value.has_referral_form;
		
		if (isReferred && !hasReferral) {
			return false;
		}
	}

	return true;
}

export function anyStudentsNeedReferrals(students) {
	if (!students) {
		return false;
	}

	if (Array.isArray(students) && students.length === 0) {
		return false;
	}

	for (const [key, value] of Object.entries(students)) {
		if (value.is_referred || (value.has_physical_competency == 1 && value.physical_competency_result == 2)) {
			return true;
		}
	}

	return false;
}

export function allHealthQuestionnairesChecked(students) {
	if (!students) {
		return false;
	}

	if (Array.isArray(students) && students.length === 0) {
		return false;
	}

	for (const [key, value] of Object.entries(students)) {
		if (!value.health_form_id) {
			return false;
		}

		if (value.health_form_id && !value.health_form_checked) {
			return false;
		}
	}

	return true;
}

export function allAssessmentsMarked(students) {
	if (!students) {
		return false;
	}

	if (Array.isArray(students) && students.length === 0) {
		return false;
	}

	for (const [key, value] of Object.entries(students)) {
		if (value.is_anonymous) {
			continue;
		}

		let assessment = value?.written_assessment;
		if (!assessment) {
			return false;
		}

		if (assessment && assessment?.submitted_at && !assessment?.tutor_marked_at) {
			return false;
		}
	}

	return true;
}

export function allPhysicalCompetencyFormsCompleted(students) {
	if (!students) {
		return false;
	}

	if (Array.isArray(students) && students.length === 0) {
		return false;
	}

	for (const [key, value] of Object.entries(students)) {
		if (value.is_anonymous) {
			continue;
		}

		if (!value.physical_competency_form_id) {
			return false;
		}
	}

	return true;
}

export function courseHasModule(course, module_name) {
	if (!course || !course.pmva_modules) {
		return false;
	}
	return course.pmva_modules.includes(module_name);
}

export function isLastDayOfCourse(course) {
	if (course?.end_date) {
		const endDate = new Date(course.end_date);
		const currentDate = new Date();
		return endDate.toDateString() == currentDate.toDateString();
	}
	return false;
}